import React, { useState, useEffect } from 'react';
import { CreatableSelect } from 'src/components/common/Select';
import {
  Container, Button, OverlayTrigger, Table, Tooltip, Popover
} from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import moment from 'moment';

import UserSettingsFetcher from 'src/fetchers/UserSettingsFetcher';

const Affiliations = () => {
  const [affiliations, setAffiliations] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);
  const [orgOptions, setOrgOptions] = useState([]);
  const [deptOptions, setDeptOptions] = useState([]);
  const [groupOptions, setGroupOptions] = useState([]);
  const [inputError, setInputError] = useState({});
  const [errorMsg, setErrorMsg] = useState('');
  const [showConfirmIndex, setShowConfirmIndex] = useState(null);
  const [inputValues, setInputValues] = useState({});

  const currentEntries = affiliations.filter((entry) => entry.current);

  const getAllAffiliations = () => {
    UserSettingsFetcher.getAllAffiliations()
      .then((data) => {
        setAffiliations(data.map((item) => (
          {
            ...item,
            disabled: true,
            current: item.from !== null && item.to === null
          }
        )));
        setErrorMsg('');
        setInputError({});
      });
  };

  useEffect(() => {
    UserSettingsFetcher.getAutoCompleteSuggestions('countries')
      .then((data) => {
        setCountryOptions(data.map((item) => ({ value: item.value, label: item.label })));
      })
      .catch((error) => {
        console.log(error);
        setInputError({});
      });

    UserSettingsFetcher.getAutoCompleteSuggestions('organizations')
      .then((data) => {
        setOrgOptions(data.map((item) => ({ value: item.value, label: item.label })));
      })
      .catch((error) => {
        console.log(error);
      });

    UserSettingsFetcher.getAutoCompleteSuggestions('departments')
      .then((data) => {
        setDeptOptions(data.map((item) => ({ value: item.value, label: item.label })));
      })
      .catch((error) => {
        console.log(error);
      });

    UserSettingsFetcher.getAutoCompleteSuggestions('groups')
      .then((data) => {
        setGroupOptions(data.map((item) => ({ value: item.value, label: item.label })));
      })
      .catch((error) => {
        console.log(error);
      });
    getAllAffiliations();
  }, []);

  const handleCreateOrUpdateAffiliation = (index) => {
    const params = affiliations[index];
    const callFunction = params.id ? UserSettingsFetcher.updateAffiliation : UserSettingsFetcher.createAffiliation;

    callFunction(params)
      .then(() => getAllAffiliations())
      .catch((error) => {
        console.error(error);
      });
  };

  const handleDeleteAffiliation = (index) => {
    const { id } = affiliations[index];
    if (id) {
      UserSettingsFetcher.deleteAffiliation(id)
        .then((result) => {
          if (result?.error) {
            console.error(result.error);
            return false;
          }
          getAllAffiliations();
          return true;
        });
    } else {
      affiliations.splice(index, 1);
      setAffiliations(affiliations);
      setInputValues({});
    }
  };

  const onChangeHandler = (index, field, value) => {
    const updatedAffiliations = [...affiliations];
    updatedAffiliations[index][field] = value;
    const newInputErrors = { ...inputError };
    if (field === 'from' && (updatedAffiliations[index].from === null || updatedAffiliations[index].from === '')) {
      newInputErrors[index] = { ...newInputErrors[index], from: true };
      setErrorMsg('From date is Required');
    } else if (field === 'to' && updatedAffiliations[index].from > value) {
      newInputErrors[index] = { ...newInputErrors[index], to: true };
      setErrorMsg('Invalid date');
    } else if (field === 'organization' && !value) {
      newInputErrors[index] = { ...newInputErrors[index], organization: true };
      setErrorMsg('Organization is required');
    } else if (newInputErrors[index]) {
      delete newInputErrors[index][field];
      if (Object.keys(newInputErrors[index]).length === 0) {
        delete newInputErrors[index];
      }
    }
    setInputError(newInputErrors);
    setAffiliations(updatedAffiliations);
  };

  const handleSaveButtonClick = (index) => {
    const updatedAffiliations = [...affiliations];
    const newInputErrors = { ...inputError };

    if (!updatedAffiliations[index].organization) {
      newInputErrors[index] = { ...newInputErrors[index], organization: true };
      setInputError(newInputErrors);
      setErrorMsg('Organization is required');
      return;
    }
    if (!updatedAffiliations[index].from) {
      newInputErrors[index] = { ...newInputErrors[index], from: true };
      setInputError(newInputErrors);
      setErrorMsg('From date is required');
      return;
    }

    if (!newInputErrors[index] || !Object.keys(newInputErrors[index]).length) {
      updatedAffiliations[index].disabled = true;
      setAffiliations(updatedAffiliations);
      handleCreateOrUpdateAffiliation(index);
    }
  };

  const popover = (index) => (
    <Popover id={`delete-confirm-${index}`}>
      <Popover.Header as="h3">Delete Confirmation</Popover.Header>
      <Popover.Body>
        Are you sure you want to delete this affiliation?
        <div className="mt-2 d-flex justify-content-center">
          <Button
            variant="danger"
            size="sm"
            onClick={() => {
              handleDeleteAffiliation(index);
              setShowConfirmIndex(null);
            }}
          >
            Yes
          </Button>
          <Button
            variant="secondary"
            size="sm"
            className="ms-2"
            onClick={() => setShowConfirmIndex(null)}
          >
            No
          </Button>
        </div>
      </Popover.Body>
    </Popover>
  );

  return (
    <Container className="my-3 d-flex flex-column gap-3">
      <h3>My affiliations </h3>
      <div>
        <h4 className="fs-5 mb-3"> Current affiliations</h4>
        <div className="d-flex flex-wrap gap-2">
          {currentEntries.map((entry) => (
            <div
              key={entry.id}
              className="border border-gray-300 rounded-2 p-2 shadow-sm mw-40 flex-grow-1"
              style={{ minWidth: '200px', maxWidth: '350px' }}
            >
              <p>
                <strong className="me-1">Country:</strong>
                {entry.country}
              </p>
              <p>
                <strong className="me-1">Organization:</strong>
                {entry.organization}
              </p>
              <p>
                <strong className="me-1">Department:</strong>
                {entry.department}
              </p>
              <p>
                <strong className="me-1">Group:</strong>
                {entry.group}
              </p>
              <p>
                <strong className="me-1">From:</strong>
                {entry.from}
              </p>
            </div>
          ))}
        </div>
      </div>

      <div className="d-flex justify-content-end my-1">
        <Button
          disabled={affiliations.some((item) => !item.id)}
          variant="primary"
          onClick={() => {
            setAffiliations((prev) => [...prev, {
              country: '',
              organization: '',
              department: '',
              group: '',
              from: '',
              to: '',
              disabled: false,
            }]);
          }}
        >
          Add affiliation
          <i className="fa fa-plus ms-1" />
        </Button>
      </div>
      <Table striped bordered hover style={{ tableLayout: 'fixed' }}>
        <colgroup>
          <col style={{ width: '20%' }} />
          <col style={{ width: '20%' }} />
          <col style={{ width: '15%' }} />
          <col style={{ width: '15%' }} />
          <col style={{ width: '12%' }} />
          <col style={{ width: '12%' }} />
          <col style={{ width: '6%' }} />
        </colgroup>
        <thead>
          <tr>
            <th>Country</th>
            <th>
              Organization
              <span className="text-danger ms-1">*</span>
            </th>
            <th>Department</th>
            <th>Working Group</th>
            <th>
              From
              <span className="text-danger ms-1">*</span>
            </th>
            <th>To</th>
            <th>Control</th>
          </tr>
        </thead>
        <tbody>

          {affiliations.map((item, index) => (
            <tr key={item.id}>
              <td>
                {item.disabled ? item.country
                  : (
                    <CreatableSelect
                      disabled={item.disabled}
                      isClearable
                      isInputEditable
                      inputValue={inputValues[`${index}_country`] || item.country || ''}
                      placeholder="Select or enter a new option"
                      options={countryOptions}
                      onCreateOption={(newValue) => {
                        const newOption = { value: newValue, label: newValue };
                        setCountryOptions((prev) => [...prev, newOption]);
                        onChangeHandler(index, 'country', newValue);
                      }}
                      value={countryOptions.find((option) => option.value === item.country) || null}
                      onChange={(choice) => {
                        const value = choice ? choice.value : '';
                        setInputValues((prev) => ({ ...prev, [`${index}_country`]: value }));
                        onChangeHandler(index, 'country', value);
                      }}
                      onInputChange={(inputValue, { action }) => {
                        if (action === 'input-change') {
                          setInputValues((prev) => ({ ...prev, [`${index}_country`]: inputValue }));
                          onChangeHandler(index, 'country', inputValue);
                        }
                      }}
                      allowCreateWhileLoading
                      formatCreateLabel={(inputValue) => `Create "${inputValue}"`}
                    />
                  )}
              </td>
              <td>
                {item.disabled ? item.organization
                  : (
                    <>
                      <CreatableSelect
                        disabled={item.disabled}
                        isClearable
                        isInputEditable
                        inputValue={inputValues[`${index}_organization`] || item.organization || ''}
                        placeholder="Select or enter a new option"
                        className={inputError[index] && inputError[index].organization ? 'is-invalid' : ''}
                        options={orgOptions}
                        value={orgOptions.find((option) => option.value === item.organization) || null}
                        onCreateOption={(newValue) => {
                          const newOption = { value: newValue, label: newValue };
                          setOrgOptions((prev) => [...prev, newOption]);
                          onChangeHandler(index, 'organization', newValue);
                        }}
                        onChange={(choice) => {
                          const value = choice ? choice.value : '';
                          setInputValues((prev) => ({ ...prev, [`${index}_organization`]: value }));
                          onChangeHandler(index, 'organization', value);
                        }}
                        onInputChange={(inputValue, { action }) => {
                          if (action === 'input-change') {
                            setInputValues((prev) => ({ ...prev, [`${index}_organization`]: inputValue }));
                            onChangeHandler(index, 'organization', inputValue);
                          }
                        }}
                        allowCreateWhileLoading
                        formatCreateLabel={(inputValue) => `Create "${inputValue}"`}
                      />
                      {inputError[index] && inputError[index].organization && (
                      <div className="invalid-feedback">Organization is required</div>
                      )}
                    </>
                  )}
              </td>
              <td>
                {item.disabled ? item.department
                  : (
                    <CreatableSelect
                      disabled={item.disabled}
                      isClearable
                      isInputEditable
                      inputValue={inputValues[`${index}_department`] || item.department || ''}
                      placeholder="Select or enter a new option"
                      options={deptOptions}
                      value={deptOptions.find((option) => option.value === item.department) || null}
                      onCreateOption={(newValue) => {
                        const newOption = { value: newValue, label: newValue };
                        setDeptOptions((prev) => [...prev, newOption]);
                        onChangeHandler(index, 'department', newValue);
                      }}
                      onChange={(choice) => {
                        const value = choice ? choice.value : '';
                        setInputValues((prev) => ({ ...prev, [`${index}_department`]: value }));
                        onChangeHandler(index, 'department', value);
                      }}
                      onInputChange={(inputValue, { action }) => {
                        if (action === 'input-change') {
                          setInputValues((prev) => ({ ...prev, [`${index}_department`]: inputValue }));
                          onChangeHandler(index, 'department', inputValue);
                        }
                      }}
                      allowCreateWhileLoading
                      formatCreateLabel={(inputValue) => `Create "${inputValue}"`}
                    />
                  )}
              </td>
              <td>
                {item.disabled ? item.group
                  : (
                    <CreatableSelect
                      placeholder="Select or enter a new option"
                      disabled={item.disabled}
                      isClearable
                      isInputEditable
                      inputValue={inputValues[`${index}_group`] || item.group || ''}
                      value={groupOptions.find((option) => option.value === item.group) || null}
                      options={groupOptions}
                      onCreateOption={(newValue) => {
                        const newOption = { value: newValue, label: newValue };
                        setGroupOptions((prev) => [...prev, newOption]);
                        onChangeHandler(index, 'group', newValue);
                      }}
                      onChange={(choice) => {
                        const value = choice ? choice.value : '';
                        setInputValues((prev) => ({ ...prev, [`${index}_group`]: value }));
                        onChangeHandler(index, 'group', value);
                      }}
                      onInputChange={(inputValue, { action }) => {
                        if (action === 'input-change') {
                          setInputValues((prev) => ({ ...prev, [`${index}_group`]: inputValue }));
                          onChangeHandler(index, 'group', inputValue);
                        }
                      }}
                      allowCreateWhileLoading
                      formatCreateLabel={(inputValue) => `Create "${inputValue}"`}
                    />
                  )}
              </td>
              <td>
                {item.disabled ? item.from
                  : (
                    <>
                      <DatePicker
                          // eslint-disable-next-line no-nested-ternary
                        placeholderText={inputError[index] ? inputError[index].from ? errorMsg : '' : 'Required'}
                        isClearable
                        clearButtonTitle="Clear"
                          // eslint-disable-next-line max-len
                        className={`py-1 rounded border ${inputError[index] && inputError[index].from ? 'border-danger' : 'border-gray'}`}
                        showPopperArrow={false}
                        disabled={item.disabled}
                        showMonthYearPicker
                        dateFormat="yyyy-MM"
                        selected={item.from}
                        onChange={(date) => {
                          onChangeHandler(index, 'from', date ? moment(date).format('YYYY-MM') : null);
                        }}
                      />
                      {inputError[index] && inputError[index].from && (
                      <div className="invalid-feedback">From is required</div>
                      )}
                    </>
                  )}
              </td>
              <td>
                {item.disabled ? item.to
                  : (
                    <DatePicker
                      placeholderText={inputError[index] && inputError[index].to ? errorMsg : ''}
                      isClearable
                      clearButtonTitle="Clear"
                        // eslint-disable-next-line max-len
                      className={`py-1 rounded border ${inputError[index] && inputError[index].to ? 'border-danger' : 'border-gray'}`}
                      showPopperArrow={false}
                      disabled={item.disabled}
                      showMonthYearPicker
                      dateFormat="yyyy-MM"
                      selected={inputError[index] && inputError[index].to ? null : item.to}
                      onChange={(date) => onChangeHandler(index, 'to', date ? moment(date).format('YYYY-MM') : date)}
                    />
                  )}
              </td>
              <td>
                <div className="d-flex justify-content-end">
                  {item.disabled
                    ? (
                      <OverlayTrigger
                        placement="top"
                        overlay={(
                          <Tooltip id="affiliation_edit_tooltip">
                            Edit affiliation
                          </Tooltip>
                          )}
                      >
                        <Button
                          size="sm"
                          variant="primary"
                          className="ms-auto"
                          onClick={() => {
                            const updatedAffiliations = [...affiliations];
                            updatedAffiliations[index].disabled = false;
                            setAffiliations(updatedAffiliations);
                          }}
                        >
                          <i className="fa fa-edit" title="Edit affiliation" />
                        </Button>
                      </OverlayTrigger>
                    )
                    : (
                      <OverlayTrigger
                        placement="top"
                        overlay={(
                          <Tooltip id="affiliation_save_tooltip">
                            Save changes
                          </Tooltip>
                          )}
                      >
                        <Button
                          size="sm"
                          variant="warning"
                          className="ms-auto"
                          onClick={() => handleSaveButtonClick(index)}
                        >
                          <i className="fa fa-save" title="Save changes" />
                        </Button>
                      </OverlayTrigger>
                    )}
                  <OverlayTrigger
                    trigger="click"
                    placement="left"
                    show={showConfirmIndex === index}
                    onToggle={(isOpen) => setShowConfirmIndex(isOpen ? index : null)}
                    overlay={popover(index)}
                  >
                    <Button
                      className="ms-1"
                      size="sm"
                      variant="danger"
                      onClick={() => setShowConfirmIndex(index)}
                    >
                      <i className="fa fa-trash-o" title="Delete affiliation" />
                    </Button>
                  </OverlayTrigger>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Container>
  );
};

export default Affiliations;
